<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    icon="fa-clipboard-list-check"
    sizeClass="modal-xl"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :submitDisabled="submitDisabled"
    :showSubmit="editing"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="assemblyItemForm" v-if="!loading">
      <ContentTitle>Item Details</ContentTitle>
      <FormRow label="Stock Type">
        <FormSelect :options="stockTypeOptions" v-model="item.stockType" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{formatLookupName(item.stockType, InventoryStockTypeLookup)}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Description">
        <FormInputText v-model="item.description" placeholder="Description of the item in the assembly" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{item.description}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Quantity" v-if="isQuantityAvailable">
        <FormInputText type="number" v-model="item.quantity" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{item.quantity}}</FormStaticLabel>
      </FormRow>
      <FormHorizontalRule />
      
      <ContentTitle>Stock</ContentTitle>
      <FormRow label="Available Stock" helpTip="If stock set, only those stock items will be available to select from. If none set, all stock items are available">
        <FormMultiSelect :options="stockListForType(item.stockType)" v-model="item.stockList" placeholder="Stock Code" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{formatMultiSelectLookupName(item.stockList, stockListForType(item.stockType)) || 'Any'}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Available Price Groups" helpTip="If price groups set, only those price groups will be available to select from. If none set, all price groups are available" v-if="isItemGlassType">
        <FormMultiSelect :options="glassPriceGroupOptions" v-model="item.pricingGroupList" placeholder="Price Group" v-show="editing" />
        <FormStaticLabel v-if="!editing">{{formatMultiSelectLookupName(item.pricingGroupList, glassPriceGroupOptions) || 'Any'}}</FormStaticLabel>
      </FormRow>
      <FormHorizontalRule />

      <ContentTitle>Settings</ContentTitle>
      <FormRow label="Is Removeable" helpTip="If removeable is not set, the item is fixed in the order and cannot be removed">
        <FormCheckbox v-model="item.isRemoveable" v-if="editing" />
        <FormStaticLabel v-if="!editing"><IconBoolean :value="item.isRemoveable" /></FormStaticLabel>
      </FormRow>
      <FormRow label="Price Overridden">
        <FormCheckbox v-model="item.isUnitPriceOverridden" v-if="editing" />
        <FormStaticLabel v-if="!editing"><IconBoolean :value="item.isUnitPriceOverridden" /></FormStaticLabel>
      </FormRow>
      <FormRow label="Unit Price" v-if="item.isUnitPriceOverridden">
        <FormInputCurrency v-model="item.unitPrice" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{formatCurrency(item.unitPrice)}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Join Type" helpTip="Join type for the corners" v-if="isAssemblySquareMetreUOM && isItemGeneralType && isItemLengthUoM">
        <FormSelect :options="StockAssemblyJoinTypeLookup" v-model="item.joinType" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{formatLookupName(item.joinType, StockAssemblyJoinTypeLookup)}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Corner Radius" v-if="isAssemblySquareMetreUOM && isItemGeneralType && isItemLengthUoM && isItemJoinTypeRounded">
        <FormInputText type="number" addon="mm" v-model="item.cornerRadius" v-if="editing" />
        <FormStaticLabel v-if="!editing">{{item.cornerRadius}}mm</FormStaticLabel>
      </FormRow>
      <FormRow label="Edgework" v-if="isAssemblySquareMetreUOM && isItemEdgeworkService" :fieldCount="4">
        <template v-slot:field1>
          <FormCheckbox label="Short A" v-if="editing" :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.shortA)" @change="itemEnableEdgeworkFlag(OrderItemEdgeworkTypes.shortA, $event)" />
          <FormStaticLabel v-if="!editing"><IconBoolean :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.shortA)" /> {{formatLookupName(OrderItemEdgeworkTypes.shortA, OrderItemEdgeworkTypeLookup)}}</FormStaticLabel>
        </template>
        <template v-slot:field2>
          <FormCheckbox label="Short B" v-if="editing" :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.shortB)" @change="itemEnableEdgeworkFlag(OrderItemEdgeworkTypes.shortB, $event)" />
          <FormStaticLabel v-if="!editing"><IconBoolean :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.shortB)" /> {{formatLookupName(OrderItemEdgeworkTypes.shortB, OrderItemEdgeworkTypeLookup)}}</FormStaticLabel>
        </template>
        <template v-slot:field3>
          <FormCheckbox label="Long A" v-if="editing" :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.longA)" @change="itemEnableEdgeworkFlag(OrderItemEdgeworkTypes.longA, $event)" />
          <FormStaticLabel v-if="!editing"><IconBoolean :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.longA)" /> {{formatLookupName(OrderItemEdgeworkTypes.longA, OrderItemEdgeworkTypeLookup)}}</FormStaticLabel>
        </template>
        <template v-slot:field4>
          <FormCheckbox label="Long B" v-if="editing" :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.longB)" @change="itemEnableEdgeworkFlag(OrderItemEdgeworkTypes.longB, $event)" />
          <FormStaticLabel v-if="!editing"><IconBoolean :value="itemHasEdgeworkFlag(OrderItemEdgeworkTypes.longB)" /> {{formatLookupName(OrderItemEdgeworkTypes.longB, OrderItemEdgeworkTypeLookup)}}</FormStaticLabel>
        </template>
      </FormRow>
      <FormRow label="Edge Offsets" :fieldCount="4" v-if="isAssemblySquareMetreUOM && isItemEdgeOffsets" helpTip="Offset from the edges of the assembly size when calculating this item size">
        <template v-slot:field1>
          <FormInputText type="number" note="Distance away from top" prepend="Top" addon="mm" v-model="item.edgeOffsetList[0]" v-if="editing" />
          <FormStaticLabel v-if="!editing">{{item.edgeOffsetList[0]}}mm</FormStaticLabel>
        </template>
        <template v-slot:field2>
          <FormInputText type="number" note="Distance away from right" prepend="Right" addon="mm" v-model="item.edgeOffsetList[1]" v-if="editing" />
          <FormStaticLabel v-if="!editing">{{item.edgeOffsetList[1]}}mm</FormStaticLabel>
        </template>
        <template v-slot:field3>
          <FormInputText type="number" note="Distance away from bottom" prepend="Bottom" addon="mm" v-model="item.edgeOffsetList[2]" v-if="editing" />
          <FormStaticLabel v-if="!editing">{{item.edgeOffsetList[2]}}mm</FormStaticLabel>
        </template>
        <template v-slot:field4>
          <FormInputText type="number" note="Distance away from left" prepend="Left" addon="mm" v-model="item.edgeOffsetList[3]" v-if="editing" />
          <FormStaticLabel v-if="!editing">{{item.edgeOffsetList[3]}}mm</FormStaticLabel>
        </template>
      </FormRow>



    </FormGroup>
  </ModalDialog>
</template>


<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'

import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypes, StockAssemblyJoinTypes, StockAssemblyJoinTypeLookup, OrderItemEdgeworkTypes, OrderItemEdgeworkTypeLookup } from '@/store/types'

export default {
  name: 'AssemblyItemDialog',
  mixins: [form, ModalDialogMixin],
  components: {
    ModalDialog,
  },
  props: {
    isCreateMode: {
      type: Boolean,
      default: false
    },
    editing: Boolean,
    assemblyUOMType: String,
    assemblyItem: Object,
    parentItem: Object
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      assemblyItemForm: {},
      item: _.cloneDeep(this.assemblyItem || {}),
      stockList: [],
      glassPriceGroupList: [],
      submitting: false
    };
  },
  computed: {
    title() {
      if(!this.editing)
        return 'Assembly Item'
      return this.isCreateMode ? 'New Assembly Item' : 'Update Assembly Item'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    submitDisabled() {
      return false
    },
    stockTypeOptions() {
      // Based on parent
      if(this.parentItem) {
        if(this.parentItem.stockType == InventoryStockTypes.glass)
          return InventoryStockTypeLookup.filter(lookup => lookup.value == InventoryStockTypes.glassService || lookup.value == InventoryStockTypes.general)
      }

      // Don't add templates / glass services (unless parent is glass)
      return InventoryStockTypeLookup.filter(lookup => lookup.value != InventoryStockTypes.template && lookup.value != InventoryStockTypes.glassService)
    },
    glassPriceGroupOptions() {
      return this.glassPriceGroupList.map(priceGroup => {
        return {
          value: priceGroup.id,
          name: priceGroup.name
        }
      })
    },
    isAssemblySquareMetreUOM() {
      return this.assemblyUOMType == UOMTypes.metresq
    },
    isItemGlassType() {
      return this.item.stockType == InventoryStockTypes.glass
    },
    isItemGeneralType() {
      return this.item.stockType == InventoryStockTypes.general
    },
    isItemGlassServiceType() {
      return this.item.stockType == InventoryStockTypes.glassService
    },
    isItemEachUOM() {
      if(!this.item.stockList)
        return false

      // At least one of the stock items must be each UoM
      var stockItems = _.compact(this.item.stockList.map(stockId => {
        return this.stockList.find(stock => stock.id == stockId) 
      }))

      return stockItems.some(stock => stock.uomType == UOMTypes.each)
    },
    isItemLengthUoM() {
      if(!this.item.stockList)
        return false
      
      // At least one of the stock items must be length UoM
      var stockItems = _.compact(this.item.stockList.map(stockId => {
        return this.stockList.find(stock => stock.id == stockId) 
      }))

      return stockItems.some(stock => stock.uomType == UOMTypes.length)
    },
    isItemJoinTypeRounded() {
      return this.item.joinType == StockAssemblyJoinTypes.rounded
    },
    isItemEdgeworkService() {
      if(this.item.stockType != InventoryStockTypes.glassService)
        return false

      return this.isItemLengthUoM
    },
    isItemEdgeOffsets() {
      if(!this.isAssemblySquareMetreUOM)
        return false

      if(this.isItemGlassType)
        return true

      if(this.isItemGeneralType) {
        return this.isItemLengthUoM
      }
      return false
    },
    isQuantityAvailable() {
      // Glass is fixed to 1 item
      if(this.isItemGlassType)
        return false

      // General / Assembly stock not using each has no quantity
      if(!this.isItemEachUOM)
        return false

      return true
    }
  },
  created() {
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.StockAssemblyJoinTypeLookup = StockAssemblyJoinTypeLookup
    this.OrderItemEdgeworkTypes = OrderItemEdgeworkTypes
    this.OrderItemEdgeworkTypeLookup = OrderItemEdgeworkTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.assemblyItemForm = { }

      // Setup default item info
      if(this.isCreateMode) {
        this.item = {
          id: uuidv4(),
          description: '',
          generatedDescription: '',
          parentId: null,
          stockType: this.stockTypeOptions[0].value,
          stockList: [],
          isRemoveable: true,
          isUnitPriceOverridden: false,
          unitPrice: 0,
          pricingGroupList: [],
          quantity: 1,
          edgeOffsetList: [0, 0, 0, 0],
          edgeworkFlagList: [],
          joinType: StockAssemblyJoinTypes.corner,
          cornerRadius: 0,
          childItems: []
        }
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      // Create generated description based on stock items
      this.item.generatedDescription = ''

      if(this.item.stockList.length > 1) {
        this.item.generatedDescription = `${this.item.stockList.length} Stock Item(s)`
      }

      if(this.item.stockList.length == 1) {
        this.item.generatedDescription = this.formatMultiSelectLookupName(this.item.stockList, this.stockListForType(this.item.stockType))

        if(this.isItemGlassType) {
          if(this.item.pricingGroupList.length == 1) {
            this.item.generatedDescription += ' - ' + this.formatMultiSelectLookupName(this.item.pricingGroupList, this.glassPriceGroupOptions)
          }
        }
      }


      this.modalClosed(this.item)      
      this.hide()
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.inventoryService.getStockList()
      ])
      .then(results => {
        this.glassPriceGroupList = results[0].data
        this.stockList = results[1].data.filter(stock => stock.isActive)
      })
      .catch(err => {
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    stockListForType(stockType) {
      return this.stockList.filter(stock => stock.type == stockType).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    itemHasEdgeworkFlag(flagType) {
      var flagList = this.item.edgeworkFlagList || []
      return flagList.includes(flagType)
    },
    itemEnableEdgeworkFlag(flagType, enable) {
      var flagList = this.item.edgeworkFlagList || []
      if(!flagList.includes(flagType) && enable)
        flagList.push(flagType)
      else if(flagList.includes(flagType) && !enable)
        flagList = flagList.filter(fl => fl != flagType)

      this.item.edgeworkFlagList = flagList
    },
    
  }
}
</script>